import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useDrag, useDrop } from 'react-dnd';

import { Card, Typography } from '@mui/material';
import { Box, styled } from '@mui/system';

import * as ItemUtils from '../../common/ItemTools';
import { isItemHidden } from '../../common/Visibility';
import useCardMenu from '../common/card/useCardMenu';
import useCardDensity, { CardDensityProvider } from '../common/card/useCardDensity';
import { EXCERPT_TEXT, SERIF_STACK } from '../common/tokens';
import VerdictBar, { useVerdictTransition } from '../common/VerdictBar';
import AddItemToListDialog from '../AddItemToListDialog';
import SelectionOverlay from '../SelectionOverlay';
import { selectIsProposedTag, selectTagVocabulary } from '../../redux/features/tags/tagsSlice';
import { updateItem } from '../../redux/features/items/itemsSlice';
import {
  selectSelectionMode, selectSelectedIds, toggleSelection,
} from '../../redux/features/context/contextSlice';

import CardActions from './CardActions';
import CardMeta from './CardMeta';
import CardPhoto from './CardPhoto';
import CardTags from './CardTags';
import ExpandPanel from './ExpandPanel';
import buildMenuOptions from './menuOptions';
import useCardTextFit from './useCardTextFit';
import { getItemMetrics } from './utils';

const StyledCard = styled(Card)(() => ({
  // The photo's avatars size themselves in `cqw` against this.
  containerType: 'inline-size',
  background: '#fff',
  borderRadius: 8,
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative',
  '&:hover': {
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
  },
}));

const CardBody = styled(Box)({
  padding: '10px 14px 6px',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
});

// Sits between the photo and the footer. Establishes the positioning
// context for the expand panel.
const ContentRegion = styled(Box)({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
});

const Title = styled(Typography)({
  fontFamily: SERIF_STACK,
  fontWeight: 700,
  fontSize: 20,
  lineHeight: 1.4,
  margin: '0 0 4px',
  color: '#1a1a1a',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textWrap: 'pretty',
  cursor: 'pointer',
});

const Excerpt = styled(Typography)({
  fontFamily: SERIF_STACK,
  fontSize: 14,
  lineHeight: 1.55,
  color: EXCERPT_TEXT,
  margin: 0,
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  whiteSpace: 'pre-line',
  cursor: 'pointer',
});

// Wraps the meta row, title, and excerpt. The excerpt's dynamic clamp is
// computed against this wrapper's height minus the title and meta heights,
// so the excerpt fills exactly the leftover vertical space.
const TextWrapper = styled(Box)({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

// Invisible clone of the excerpt used to measure the natural rendered
// line count (no clamp, out of flow). The card uses this to pick a title
// font tier — short excerpts → bigger title to absorb the empty space.
const ExcerptMeasure = styled('div')({
  position: 'absolute',
  left: 0,
  right: 0,
  top: 0,
  visibility: 'hidden',
  pointerEvents: 'none',
  fontFamily: SERIF_STACK,
  fontSize: 14,
  lineHeight: 1.55,
  whiteSpace: 'pre-line',
});

const Footer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexShrink: 0,
});

const VerdictRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  paddingTop: 8,
  marginTop: 4,
  borderTop: '1px solid #f0f0f0',
});

export default function ItemCard(props) {
  const {
    isPreview,
    namespace,
    onAuthorView,
    onAuthorFilter,
    onSiteView,
    onSiteFilter,
    onItemMarkReviewed,
    onItemVerdict,
    onItemBookmark,
    onItemClone,
    onItemEdit,
    onItemHide,
    onItemDelete,
    onItemUnshare,
    onItemFavorite,
    onItemRead,
    onItemShare,
    onTagOpen,
    onTypeView,
    onItemMerge,
  } = props;
  const { item } = props;

  const dispatch = useDispatch();
  const selectionMode = useSelector(selectSelectionMode);
  const selectedIds = useSelector(selectSelectedIds);
  const isSelected = selectedIds.includes(item.id);
  const isModerator = useSelector((state) => Boolean(state.users.userInfo?.isModerator));

  // Tags land on private copy of the item even when browsed in public.
  // Pending cards are always moderating, this never runs there.
  const addTag = (tag) => {
    if (!tag || !namespace) return;

    // Send update to server - context will be updated automatically
    const tags = item.tags ? [...item.tags, tag] : [tag];
    const tagIds = tags.map((t) => t.id);
    const patch = { tagIds };
    dispatch(updateItem({ namespace: 'private', itemId: item.id, patch }));
  };

  const rawUserTags = useSelector(selectTagVocabulary);

  const isProposedTag = useSelector(selectIsProposedTag);
  const isProposed = namespace === 'pending' ? isProposedTag : null;

  const {
    wrapperRef, metaRef, titleRef, excerptRef, measureRef,
    titleSize, excerptSize, excerptStyle, excerptReady,
  } = useCardTextFit(item.excerpt);

  const { cardRef, shows } = useCardDensity();
  const [addingTag, setAddingTag] = React.useState(false);

  const [expanded, setExpanded] = React.useState(false);
  const handleExpandClick = React.useCallback(() => {
    setExpanded(!expanded);
  }, [expanded, setExpanded]);

  const onToggleSelect = () => dispatch(toggleSelection(item.id));

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const handleListDialogClose = () => {
    setDialogOpen(false);
  };
  const [, drag] = useDrag(() => ({
    type: 'box',
    item: { name: item.id },
    end: (dragItem, monitor) => {
      const dropResult = monitor.getDropResult();
      if (dragItem && dropResult) {
        onItemMerge(dropResult.name, [dragItem.name]);
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
      handlerId: monitor.getHandlerId(),
    }),
  }));

  const [, drop] = useDrop(() => ({
    accept: 'box',
    drop: () => ({ name: item.id }),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }));

  // The root is both the drag source and what the ladder measures.
  const setCardNode = React.useCallback((node) => {
    cardRef.current = node;
    drag(node);
  }, [cardRef, drag]);

  // Moderation footer: approve/reject become the card's primary actions and
  // everything else is demoted behind the overflow menu.
  const moderating = !isPreview && Boolean(onItemVerdict);
  const showVerdictBar = moderating && !selectionMode;
  const handleDecide = React.useCallback(
    (kind, reason) => onItemVerdict && onItemVerdict(item, kind, reason),
    [onItemVerdict, item],
  );
  const { decide, sx: verdictSx } = useVerdictTransition(handleDecide);

  const isHidden = isItemHidden(item);

  const canAddTag = !isPreview && !moderating;
  const menuOptions = buildMenuOptions({
    item,
    moderating,
    isHidden,
    collapsed: !shows('actions'),
    composerCollapsed: canAddTag && !shows('actions'),
    onItemEdit,
    onItemClone,
    onItemHide,
    onItemDelete,
    onItemUnshare,
    onItemBookmark,
    onItemShare,
    onItemMarkReviewed,
    onItemFavorite,
    onAddToList: () => setDialogOpen(true),
    onAddTag: () => setAddingTag(true),
  });

  const { open: openMenu, menu } = useCardMenu(menuOptions);

  const metrics = getItemMetrics(item);
  const logoUrl = ItemUtils.getLogoUrl(item);
  const sourceLetter = ItemUtils.getSourceLetter(item);
  const siteId = ItemUtils.getPrimarySiteId(item);
  const site = ItemUtils.getPrimarySite(item);
  const domain = ItemUtils.getDomain(item);
  const firstAuthor = (item.authors && item.authors[0]) || null;
  const titleAuthorName = firstAuthor ? ItemUtils.getAuthorName(firstAuthor) : '';

  const { heatScore, buzzScore } = ItemUtils.getHeatBuzz(item);

  return (
    <CardDensityProvider shows={shows}>
      <StyledCard
        ref={setCardNode}
        raised
        sx={moderating ? verdictSx : undefined}
      >
        <SelectionOverlay
          selectionMode={selectionMode}
          selected={isSelected}
          onToggle={onToggleSelect}
        />
        <CardPhoto
          ref={drop}
          item={item}
          sourceLetter={sourceLetter}
          titleAuthorName={titleAuthorName}
          metrics={metrics}
          heatScore={heatScore}
          buzzScore={buzzScore}
          onItemRead={onItemRead}
          onTypeView={onTypeView}
          onExpandClick={isModerator ? handleExpandClick : null}
        />
        <CardBody>
          <ContentRegion>
            <TextWrapper ref={wrapperRef}>
              <CardMeta
                ref={metaRef}
                authors={item.authors}
                titleAuthorName={titleAuthorName}
                site={site}
                siteId={siteId}
                domain={domain}
                logoUrl={logoUrl}
                sourceLetter={sourceLetter}
                metrics={metrics}
                links={item.links}
                extraLinks={item.extra && item.extra.links}
                onAuthorView={onAuthorView}
                onAuthorFilter={onAuthorFilter}
                onSiteView={onSiteView}
                onSiteFilter={onSiteFilter}
              />
              <Title
                ref={titleRef}
                variant="h3"
                style={{ fontSize: titleSize }}
                onClick={() => onItemRead && onItemRead(item)}
              >
                {item.title}
              </Title>
              {item.excerpt && shows('excerpt') && (
                <Excerpt
                  ref={excerptRef}
                  variant="body2"
                  style={{ ...excerptStyle, fontSize: excerptSize }}
                  onClick={() => onItemRead && onItemRead(item)}
                >
                  {excerptReady ? item.excerpt : null}
                </Excerpt>
              )}
            </TextWrapper>
            {item.excerpt && shows('excerpt') && (
              <ExcerptMeasure ref={measureRef}>{item.excerpt}</ExcerptMeasure>
            )}
            <ExpandPanel
              item={item}
              expanded={expanded}
              heatScore={heatScore}
              buzzScore={buzzScore}
              onItemRead={onItemRead}
            />
          </ContentRegion>
          <Footer>
            <CardTags
              tags={item.tags}
              onTagOpen={onTagOpen}
              isProposed={isProposed}
              canAdd={canAddTag}
              vocabulary={rawUserTags}
              onAddTag={addTag}
              relatedItems={item.relatedItems}
              onItemRead={onItemRead}
              addingTag={addingTag}
              onAddingTag={setAddingTag}
            />
            {!moderating && (
              <CardActions
                item={item}
                isPreview={isPreview}
                selectionMode={selectionMode}
                isHidden={isHidden}
                hasMenu={menuOptions.length > 0}
                onMenuClick={openMenu}
                onItemBookmark={onItemBookmark}
                onItemShare={onItemShare}
                onItemHide={onItemHide}
                onItemMarkReviewed={onItemMarkReviewed}
                onItemFavorite={onItemFavorite}
              />
            )}
          </Footer>
          {showVerdictBar && (
            <VerdictRow>
              <VerdictBar onDecide={decide} menuOptions={menuOptions} />
            </VerdictRow>
          )}
        </CardBody>
        {menu}
        <AddItemToListDialog
          open={dialogOpen}
          onClose={handleListDialogClose}
          item={item}
        />
      </StyledCard>
    </CardDensityProvider>
  );
}

ItemCard.propTypes = {
  isPreview: PropTypes.bool,
  namespace: PropTypes.string,
  onItemMarkReviewed: PropTypes.func,
  onItemVerdict: PropTypes.func,
  onItemEdit: PropTypes.func,
  onItemDelete: PropTypes.func,
  onItemUnshare: PropTypes.func,
  onItemFavorite: PropTypes.func,
  onItemRead: PropTypes.func,
  onItemShare: PropTypes.func,
  onTagOpen: PropTypes.func,
  onTypeView: PropTypes.func,
};

ItemCard.defaultProps = {
  isPreview: false,
  namespace: null,
  onItemMarkReviewed: null,
  onItemVerdict: null,
  onItemEdit: null,
  onItemDelete: null,
  onItemUnshare: null,
  onItemFavorite: null,
  onItemRead: null,
  onItemShare: null,
  onTagOpen: null,
  onTypeView: null,
};
